'use client';
import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import PlayerCountStep from './PlayerCountStep';
import PlayerConfigStep from './PlayerConfigStep';
import RulesStep from './RulesStep';
import DiceRollStep from './DiceRollStep';
import {
  PLAYER_COLORS,
  getPieceDistribution,
  type AIDifficulty,
  type GameRules,
  createNewGame,
} from '@/lib/gameEngine';

export type PlayerConfig = {
  id: string;
  name: string;
  isAI: boolean;
  aiDifficulty: AIDifficulty;
  colorIndex: number;
};

export type SetupStep = 'count' | 'players' | 'rules' | 'dice';

const DEFAULT_RULES: GameRules = {
  balancingPhase: true,
  texasTieBreak: true,
  sweetwaterRule: false,
  chunnelRule: false,
};

const STEP_LABELS: Record<SetupStep, string> = {
  count: 'Players',
  players: 'Configure',
  rules: 'Rules',
  dice: 'Roll Order',
};

const STEP_ORDER: SetupStep[] = ['count', 'players', 'rules', 'dice'];

export default function GameSetupClient() {
  const router = useRouter();
  const [step, setStep] = useState<SetupStep>('count');
  const [playerCount, setPlayerCount] = useState(2);
  const [players, setPlayers] = useState<PlayerConfig[]>([
    { id: 'player-1', name: 'Player 1', isAI: false, aiDifficulty: 'medium', colorIndex: 0 },
    { id: 'player-2', name: 'Player 2', isAI: false, aiDifficulty: 'medium', colorIndex: 1 },
  ]);
  const [rules, setRules] = useState<GameRules>(DEFAULT_RULES);
  const [turnOrder, setTurnOrder] = useState<number[]>([]);

  const handlePlayerCountSelect = useCallback((count: number) => {
    setPlayerCount(count);
    const distribution = getPieceDistribution(count);
    const newPlayers: PlayerConfig[] = Array.from({ length: count }, (_, i) => ({
      id: `player-${i + 1}`,
      name: players[i]?.name ?? `Player ${i + 1}`,
      isAI: players[i]?.isAI ?? false,
      aiDifficulty: players[i]?.aiDifficulty ?? 'medium',
      colorIndex: i,
    }));
    setPlayers(newPlayers);
    setStep('players');
    // distribution used for display only — actual distribution in engine
    void distribution;
  }, [players]);

  const handleStartGame = useCallback((orderedIndices: number[]) => {
    setTurnOrder(orderedIndices);

    // Build ordered player configs based on dice roll order
    const orderedPlayers = orderedIndices.map((idx) => players[idx]);

    const gameState = createNewGame(
      orderedPlayers.map((p) => ({
        name: p.name,
        isAI: p.isAI,
        aiDifficulty: p.isAI ? p.aiDifficulty : undefined,
        color: PLAYER_COLORS[p.colorIndex].color,
        colorVar: PLAYER_COLORS[p.colorIndex].colorVar,
      })),
      rules
    );

    // Backend integration point: persist gameState to server/DB here
    if (typeof window !== 'undefined') {
      sessionStorage.setItem('vinciGameState', JSON.stringify(gameState));
    }

    router.push('/game-board');
  }, [players, rules, router]);

  const currentStepIndex = STEP_ORDER.indexOf(step);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Nav */}
      <nav className="flex items-center justify-between px-6 py-5 lg:px-12 border-b border-border">
        <a href="/" className="flex items-center gap-3">
          <AppLogo size={32} />
          <span className="font-700 text-foreground text-lg">ViNCi</span>
        </a>
        <div className="flex items-center gap-3">
          <a
            href="/settings"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-border text-xs font-600 text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all duration-150"
            title="Settings"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="hidden sm:inline">Settings</span>
          </a>
          <span className="text-sm text-muted-foreground font-500">Game Setup</span>
        </div>
      </nav>

      {/* Step progress */}
      <div className="px-6 py-6 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-0">
            {STEP_ORDER.map((s, i) => {
              const isCompleted = i < currentStepIndex;
              const isCurrent = s === step;
              return (
                <React.Fragment key={`step-indicator-${s}`}>
                  <div className="flex flex-col items-center gap-1.5">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-700 transition-all duration-300 ${
                        isCompleted
                          ? 'bg-primary text-primary-foreground'
                          : isCurrent
                          ? 'bg-primary/20 text-primary border-2 border-primary' :'bg-muted text-muted-foreground'
                      }`}
                    >
                      {isCompleted ? (
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        i + 1
                      )}
                    </div>
                    <span
                      className={`text-xs font-500 whitespace-nowrap ${
                        isCurrent ? 'text-primary' : isCompleted ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {STEP_LABELS[s]}
                    </span>
                  </div>
                  {i < STEP_ORDER.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 mb-5 transition-all duration-300 ${
                        i < currentStepIndex ? 'bg-primary' : 'bg-border'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>

      {/* Step content */}
      <div className="flex-1 px-6 pb-12 lg:px-12">
        <div className="max-w-2xl mx-auto">
          {step === 'count' && (
            <PlayerCountStep
              selected={playerCount}
              onSelect={handlePlayerCountSelect}
            />
          )}
          {step === 'players' && (
            <PlayerConfigStep
              players={players}
              onUpdatePlayers={setPlayers}
              onNext={() => setStep('rules')}
              onBack={() => setStep('count')}
            />
          )}
          {step === 'rules' && (
            <RulesStep
              rules={rules}
              onUpdateRules={setRules}
              onNext={() => setStep('dice')}
              onBack={() => setStep('players')}
            />
          )}
          {step === 'dice' && (
            <DiceRollStep
              players={players}
              onStart={handleStartGame}
              onBack={() => setStep('rules')}
            />
          )}
        </div>
      </div>
    </div>
  );
}